import { useEffect, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";
import "react-bootstrap-typeahead/css/Typeahead.css";
import {
  Flex,
  Box,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Portal,
  Text,
} from "@chakra-ui/react";
import { ChevronDownIcon } from "@chakra-ui/icons";
import { TbLogout } from "react-icons/tb";
import { getUserAccess, hasPermission } from "../../utils/authUtils";
import { flattenHrmsSubTabs } from "../../Pages/employee/hrmsNav";

interface SubTab {
  key?: string;
  label: string;
  path: string;
  adminOnly?: boolean;
  /** When set, visibility requires at least one of these hrmsPermissions keys. Applies in addition to adminOnly. */
  requiredPermission?: string | string[];
  /** When set, the sidebar dropdown renders items under this group heading. */
  group?: string;
}

const availableTabs = [
  {
    key: "bookings",
    label: "Bookings",
    subTabs: [
      { label: "Quick View", path: "/" },
      { label: "Master View", path: "/bookings/masterview" },
      { label: "Amended App Bookings", path: "/bookings/modified" },
      { label: "Provisional Bookings", path: "/provisional/bookings" },
      { label: "Single Bookings", path: "/single/bookings" },
      { label: "Subscriptions", path: "/subscriptions" },
    ],
  },
  {
    key: "experts",
    label: "Experts",
    subTabs: [
      { label: "App Experts", path: "/experts" },
      { key: "b2bExperts", label: "B2B Workshop Experts", path: "/expertsb2b" },
      // { label: "Fuji Experts", path: "/programme-experts" },
      { label: "App Experts Onboarding", path: "/onboarding-experts" },
      { label: "Treatment Plans", path: "/experts/treatmentplans" },
      {
        label: "Pre-session Questions",
        path: "/presession-questions",
        adminOnly: true,
      },
    ],
  },
  { key: "corporate", path: "/corporate", label: "Corporate" },

  {
    key: "workshops",
    label: "Workshops",
    subTabs: [
      { key: "b2bWorkshop", label: "B2B Workshop", path: "/workshopsb2b" },
      { label: "B2C Workshop", path: "/workshops" },
    ],
  },
  {
    key: "freeconsultation",
    label: "Free Consultation",
    subTabs: [
      // {
      //   label: "Free Consultation (Old)",
      //   path: "/freeconsultationcurrent",
      // },
      { label: "Free Consultation", path: "/freeconsultation" },
      { label: "Free Consultation (Admin)", path: "/freeconsultation/admin" },
    ],
  },
  {
    key: "payments",
    path: "/assessment/payments",
    label: "Payments",
  },

  // {
  //   key: "notifications",
  //   label: "Notifications",
  //   subTabs: [
  //     { label: "App Notification", path: "/notifications" },
  //     { label: "Whatsapp Notifications", path: "/notifications/whatsapp" },
  //     {
  //       label: "App Notification Logs",
  //       path: "/notifications/logs",
  //     },
  //     {
  //       label: "Whatsapp Notification Logs",
  //       path: "/notifications/whatsapp/logs",
  //     },
  //   ],
  // },
  {
    key: "manoshalacare",
    label: "Manoshala Care",
    subTabs: [
      { label: "Chat", path: "/manoshalacare/chat" },
      { label: "Support", path: "/manoshalacare/support" },
      // {
      //   label: "Free Therapy Request",
      //   path: "/manoshalacare/freetherapyrequest",
      // },
    ],
  },
  {
    key: "reachout",
    label: "Reports",
    path: "/resources/reports",

    // subTabs: [
    //   { label: "Assessments", path: "/resources/assessments" },
    //   { label: "App Users", path: "/resources/users" },
    //   { label: "Wix Assessment", path: "/resources/wix-assessment" },
    //   { label: "Reports", path: "/resources/reports" },
    // ],
  },
  {
    key: "paidplans",
    label: "Paid Plans",
    subTabs: [
      { label: "Therapy Plus", path: "/paidplans/therapyplus" },
      { label: "Pro", path: "/paidplans/pro" },
    ],
  },
  {
    key: "billing",
    label: "Billing",
    subTabs: [
      { label: "User Billing", path: "/billing/userbilling" },
      { label: "Expert Billing", path: "/billing/expertbilling" },
    ],
  },
  {
    key: "hrms",
    label: "HRMS",
    // Grouped structure lives in src/Pages/employee/hrmsNav.ts so the sidebar
    // and the in-page HRMS sub-nav stay in sync.
    subTabs: flattenHrmsSubTabs(),
  },
  // {
  //   key: "programmeexperts",
  //   path: "/programme-experts",
  //   label: "Fuji Experts",
  // },
  // { key: "siffclients", path: "/siff-clients", label: "SIFF" },
];

const SubTabItem = ({
  subTab,
  navigate,
  isActive,
}: {
  subTab: SubTab;
  navigate: (path: string) => void;
  isActive?: boolean;
}) => (
  <MenuItem
    bg={isActive ? "#2a5f5f" : "#327171"}
    color={isActive ? "#fdb508" : "white"}
    fontWeight={isActive ? "600" : "normal"}
    borderLeft={isActive ? "3px solid #fdb508" : "3px solid transparent"}
    _hover={{ color: "#fdb508", bg: "#2a5f5f" }}
    onClick={() => navigate(subTab.path)}
  >
    {subTab.label}
  </MenuItem>
);

/**
 * Renders a dropdown's grouped sub-tabs (HRMS) as one entry per group. The
 * items inside a group are not listed here: each HRMS page shows them as
 * tabs (HrmsPageShell), so a group entry opens its first visible page.
 */
const GroupedSubTabs = ({
  subTabs,
  navigate,
  currentPath,
}: {
  subTabs: SubTab[];
  navigate: (path: string) => void;
  currentPath: string;
}) => {
  const groups = useMemo(() => {
    const result: { label: string; items: SubTab[] }[] = [];
    subTabs.forEach((subTab) => {
      const label = subTab.group || "";
      let group = result.find((g) => g.label === label);
      if (!group) {
        group = { label, items: [] };
        result.push(group);
      }
      group.items.push(subTab);
    });
    return result;
  }, [subTabs]);

  return (
    <>
      {groups.map((group) => (
        <SubTabItem
          key={group.label}
          subTab={{ ...group.items[0], label: group.label }}
          navigate={navigate}
          isActive={group.items.some(
            (item) =>
              currentPath === item.path ||
              currentPath.startsWith(item.path + "/"),
          )}
        />
      ))}
    </>
  );
};

const renderSubTabs = (
  subTabs: SubTab[],
  navigate: (path: string) => void,
  currentPath: string,
) => {
  const hasGroups = subTabs.some((s) => s.group);
  if (!hasGroups) {
    return subTabs.map((subTab) => (
      <SubTabItem
        key={subTab.path}
        subTab={subTab}
        navigate={navigate}
        isActive={
          currentPath === subTab.path ||
          currentPath.startsWith(subTab.path + "/")
        }
      />
    ));
  }

  return (
    <GroupedSubTabs
      subTabs={subTabs}
      navigate={navigate}
      currentPath={currentPath}
    />
  );
};

export const ManoshalaNavbar = () => {
  const accessType = localStorage.getItem("logged_in_user_type") || "";
  const showAllTabs = accessType === "admin";
  const storedPermissions = localStorage.getItem("logged_in_user_permissions");

  const userPermissions = storedPermissions
    ? JSON.parse(storedPermissions)
    : [];

  const accessControls =
    JSON.parse(localStorage.getItem("logged_in_user_accesscontrols") || "[]") ||
    [];

  const { isAdmin } = getUserAccess();

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!showAllTabs && !userPermissions.includes("bookings")) {
      const nextTab: any = availableTabs.find(
        (tab) => showAllTabs || userPermissions.includes(tab.key)
      );

      // let nextTab: any = availableTabs.find(
      //   (tab) => showAllTabs || userPermissions.includes(tab.key)
      // );

      // let allowedSubTab: any = null;

      // if (!nextTab) {
      //   for (const tab of availableTabs) {
      //     if (!tab.subTabs) continue;

      //     const match = tab.subTabs.find(
      //       (subTab) => subTab.key && userPermissions.includes(subTab.key)
      //     );

      //     if (match) {
      //       nextTab = tab;
      //       allowedSubTab = match;
      //       break;
      //     }
      //   }
      // }

      if (nextTab) {
        navigate(
          nextTab.path || nextTab.subTabs[0].path
          // nextTab.path || allowedSubTab?.path || nextTab.subTabs[0].path
        );
      }
    }
  }, []);

  const handleLogout = () => {
    localStorage.clear();
    navigate("/login");
  };

  return (
    <Box
      bg="#308888"
      w="16%"
      py={10}
      px={4}
      boxShadow="md"
      top="0"
      zIndex="1000"
      position="fixed"
      height="100vh"
      left={0}
      display="flex"
      flexDirection="column"
      overflow="hidden"
    >
      <Flex direction="column" align="center" h="100%" minH={0}>
        <Flex justify="space-between" w="100%" align="center" mb={4} flexShrink={0}>
          <Box onClick={() => navigate("/")} cursor="pointer" color="white">
            <Text fontWeight="bold" fontSize="2xl" mb="-0.5">
              ManoShala
            </Text>
            <Text fontSize="sm" fontWeight="semibold">
              Live App Portal
            </Text>
          </Box>
        </Flex>
        <Flex
          direction="column"
          align="flex-start"
          justify="space-between"
          w="100%"
          flex="1"
          minH={0}
        >
          <Box w="100%" overflowY="auto" overflowX="hidden" flex="1" minH={0}>
            {availableTabs.map(
              (tab) =>
                (showAllTabs || userPermissions.includes(tab.key)) &&
                (tab.subTabs ? (
                  <Menu key={tab.key}>
                    <MenuButton as={Text} cursor="pointer" color="white">
                      <div
                        className="d-flex align-items-center justify-content-between"
                        style={{ fontSize: "16px" }}
                      >
                        {tab.label}
                        <ChevronDownIcon color="#fdb508" className="ml-4" />
                      </div>
                    </MenuButton>
                    <Portal>
                      <MenuList
                        bg="#327171"
                        color="white"
                        zIndex={1500}
                        borderRadius="none"
                        minWidth="230px"
                        maxH="80vh"
                        overflowY="auto"
                      >
                        {renderSubTabs(
                          (tab.subTabs as SubTab[]).filter((subTab: SubTab) => {
                            // Hide admin-only tabs from non-admin users
                            if (subTab.adminOnly && !isAdmin) {
                              return false;
                            }
                            // A specific permission requirement (HRMS subtabs)
                            // must also be met.
                            if (subTab.requiredPermission) {
                              const keys = Array.isArray(subTab.requiredPermission)
                                ? subTab.requiredPermission
                                : [subTab.requiredPermission];
                              if (!keys.some((key) => hasPermission(key))) {
                                return false;
                              }
                            }
                            // Existing filter for free consultation admin
                            if (
                              subTab.path === "/freeconsultation/admin" &&
                              accessControls.includes("juniorEhcExpert")
                            ) {
                              return false;
                            }
                            return true;
                          }),
                          navigate,
                          location.pathname,
                        )}
                      </MenuList>
                    </Portal>
                  </Menu>
                ) : (
                  <Text
                    key={tab.key}
                    cursor="pointer"
                    color="white"
                    onClick={() => navigate(tab.path)}
                  >
                    {tab.label}
                  </Text>
                ))
            )}
          </Box>
          <Flex
            cursor="pointer"
            fontWeight="medium"
            fontSize="lg"
            onClick={handleLogout}
            color="#fdb508"
            direction="row"
            alignItems="center"
            gap={2}
            flexShrink={0}
            pt={3}
          >
            <TbLogout size="20" />
            <Text mb="0" fontSize="md">
              Logout
            </Text>
          </Flex>
        </Flex>
      </Flex>
    </Box>
  );
};
